import { i18n } from "@/plugins/vuetify";
import { useSettingsStore } from "@/store/index";

// padEnd2
// (5) returns "5.00"
// (5.4) returns "5.40"
// (5.456) returns "5.45"
export function padEnd2(value) {
  if (!isNaN(value)) {
    const decimalPlaces = 2;
    const padEndValue = "0";
    value = value.toString();
    let result = value.split(".");
    if (result[1] && result[1].length > decimalPlaces) {
      result[1] = result[1].substring(0, decimalPlaces);
      return result[0].concat(".").concat(result[1]);
    }
    if (result[1]) {
      result[1] = result[1].padEnd(decimalPlaces, padEndValue);
      return result[0].concat(".").concat(result[1]);
    }
    const decimals = "".padEnd(decimalPlaces, padEndValue);
    return value.concat(".").concat(decimals);
  }
  return value;
}

export default function convertToCNPJ(cnpj) {
  if (!cnpj) return;
  const raw = cnpj.replace(/[^0-9]/g, "");
  if (raw === "") {
    cnpj = "";
    return;
  }
  let formatted = "";
  if (raw.length > 0) {
    formatted = raw.slice(0, 2);
  }
  if (raw.length > 2) {
    formatted += "." + raw.slice(2, 5);
  }
  if (raw.length > 5) {
    formatted += "." + raw.slice(5, 8);
  }
  if (raw.length > 8) {
    formatted += "/" + raw.slice(8, 12);
  }
  if (raw.length > 12) {
    formatted += "-" + raw.slice(12, 14);
  }
  return formatted;
}
// padEnd4
// (5) returns "5.0000"
// (5.4) returns "5.4000"
// (5.456) returns "5.4560"
export function padEnd4(value) {
  if (isNaN(value)) {
    return value;
  }
  const decimalPlaces = 4;
  const padEndValue = "0";
  value = value.toString();
  let result = value.split(".");
  if (result[1] && result[1].length > decimalPlaces) {
    result[1] = result[1].substring(0, decimalPlaces);
    return result[0].concat(".").concat(result[1]);
  }
  if (result[1]) {
    result[1] = result[1].padEnd(decimalPlaces, padEndValue);
    return result[0].concat(".").concat(result[1]);
  }
  const decimals = "".padEnd(decimalPlaces, padEndValue);
  return value.concat(".").concat(decimals);
}

// padEndJpy
// (5) returns "5.000000"
// (5.4) returns "5.400000"
// (5.456) returns "5.456000"
export function padEndJpy(value) {
  if (!isNaN(value)) {
    const decimalPlaces = 6;
    const padEndValue = "0";
    value = value.toString();
    let result = value.split(".");
    if (result[1]) {
      result[1] = result[1].padEnd(decimalPlaces, padEndValue);
      return result[0].concat(".").concat(result[1]);
    }
    const decimals = "".padEnd(decimalPlaces, padEndValue);
    return value.concat(".").concat(decimals);
  }
  return value;
}

//flow.sight
export function formatShortWeekdayDate(value) {
  if (!value) {
    return {
      date: "-",
      time: "-",
    };
  }

  const date = new Date(value);

  if (isNaN(date.getTime())) {
    return {
      date: "-",
      time: "-",
    };
  }

  const timeZone = getTimeZone();

  const dateParts = new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "2-digit",
    year: "numeric",
    timeZone,
  }).formatToParts(date);

  const getDatePart = (type) =>
    dateParts.find((p) => p.type === type)?.value || "";

  const formattedDate = `${getDatePart("weekday")}, ${getDatePart("month")} ${getDatePart("day")} ${getDatePart("year")}`;

  const formattedTime = new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone,
  }).format(date);

  const timezoneLabel = timeZone === "UTC" ? "UTC" : "GMT-03";

  return {
    date: formattedDate,
    time: `${formattedTime} (${timezoneLabel})`,
  };
}

// PRICE AND BIPS METHODS

// getPriceAndBips
// (5.1234) returns { price: "5.12", bips: "34" }
// (5.1) returns { price: "5.10", bips: "00" }
export function getPriceAndBips(price) {
  price = padEnd4(price);
  const priceSlited = price.split(".");
  const integer = priceSlited[0];
  let decimals = priceSlited[1];
  let sliceParameter = -2;
  const bips = decimals.slice(sliceParameter);
  decimals = decimals.slice(0, sliceParameter);
  price = `${integer}.${decimals}`;
  return { price: price, bips: bips };
}

// getPriceAndBips2
// (5.1234) returns { price: "5.", bips: "12" }
// (5.1) returns { price: "5.", bips: "10" }
export function getPriceAndBips2(price) {
  price = padEnd2(price);
  const priceSlited = price.split(".");
  const integer = priceSlited[0];
  let decimals = priceSlited[1];
  let sliceParameter = -2;
  const bips = decimals.slice(sliceParameter);
  decimals = decimals.slice(0, sliceParameter);
  price = `${integer}.${decimals}`;
  return { price: price, bips: bips };
}

// getPriceAndBipsJPY
// (3.456789) returns { price: "3.45", bips: "6789" }
// (123.456789) returns { price: "123.45", bips: "6789" }
export function getPriceAndBipsJPY(price) {
  price = padEndJpy(price);
  const priceSlited = price.split(".");
  const integer = priceSlited[0];
  let decimals = priceSlited[1];
  const sliceParameter = -4;
  const bips = decimals.slice(sliceParameter);
  decimals = decimals.slice(0, sliceParameter);
  price = `${integer}.${decimals}`;
  return { price: price, bips: bips };
}

// FORMAT PRICE METHODS

// formatPrice
// (1234.56) returns "1,234.56"
// (1234.56789) return "1,234.56789"
// (1234567) returns "1,234,567.00"
export function formatPrice(price) {
  if (price === null) return "";
  if (isNaN(price)) {
    return price;
  }
  if (price === "") {
    return price;
  }
  const priceStr = price.toString();
  const result = priceStr.split(".");
  if (result[1]) {
    let integer = result[0];
    const decimals = result[1];
    integer = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return integer.concat(".").concat(decimals);
  }
  return priceStr.replace(/\B(?=(\d{3})+(?!\d))/g, ",").concat(".00");
}

// formatBRLPretty
// (1234.56) returns "1.234,56"
// (1234.56789) return "1.234,57"
// (1234567) returns "1.234.567,00"
export function formatBRLPretty(price) {
  const ccy = "BRL";
  const intlNumberFormat = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: ccy,
    currencyDisplay: "code",
  });
  price = intlNumberFormat.format(price);
  price = price.replace(ccy, "").trim();
  return price;
}

// formatUSDPretty
// (1234.56) returns "1,234.56"
// (1234.56789) return "1,234.57"
// (1234567) returns "1,234,567.00"
export function formatUSDPretty(price) {
  if (!price) {
    return price;
  }
  const ccy = "USD";
  const intlNumberFormat = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: ccy,
    currencyDisplay: "code",
  });
  price = intlNumberFormat.format(price);
  price = price.replace(ccy, "").trim();
  return price;
}

// TIME & DATE METHODS

// getCurrentTime
// pega a hora atual do sistema e exibe como HH:MM:SS
export function getCurrentTime() {
  const now = new Date();
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");
  let formattedTime = `${hours}:${minutes}:${seconds}`;
  return formattedTime;
}

// getOffsetDate
// retorna a data de hoje ou deslocada em dias.
// Se forAvgPtax = false → formato YYYY-MM-DD (ISO-like).
// Se forAvgPtax = true → formato DD/MM/YYYY (estilo brasileiro).
export function getOffsetDate(offsetDays = 0, forAvgPtax = false) {
  const date = new Date();
  date.setDate(date.getDate() + offsetDays);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  if (forAvgPtax) {
    return `${day}/${month}/${year}`;
  }

  let formattedDate = `${year}-${month}-${day}`;
  return formattedDate;
}

// getCurrentDateAndTime
// returna getOffsetDate no padrão YYYY-MM-DD e getCurrentTime no padrão HH:MM:SS
export function getCurrentDateAndTime(offsetDays = 0) {
  return `${getOffsetDate(offsetDays)} ${getCurrentTime()}`;
}

// getTimeZone
// retorna a timezone selecionada pelo usuário
export function getTimeZone() {
  const { timezone } = useSettingsStore();
  return timezone === "BRT" ? "America/Sao_Paulo" : "UTC";
}

// getDateParts
// pode receber como argumento números (timestamps), strings de data, objetos Date, arrays/objetos coercentes para string ou nada (data atual)

// Se usar sem argumento, retorna as partes da hora atual do sistema

// NÚMERO é lido como timestamp em milissegundos desde 1 Jan 1970 (epoch).
// (0) → 1970-01-01

// STRING sem incluir time:
// ("2025-08-27T14:35:00Z") →
// { year: "2025", month: "08", day: "27", hour: undefined, minute: undefined }

// STRING e incluir time:
// ("2025-08-27T14:35:00Z", true) →
// { year: "2025", month: "08", day: "27", hour: "11", minute: "35" }

// OBJECT coercente
// ({ toString: () => "2025-08-27" }) é equivalente a usar a STRING "2025-08-27"

// OBJECT Date é aceito e simplesmente copiado

// ARRAY-like convertido para STRING pode funcionar, mas não é seguro:
// new Date([2025, 7, 27]) vira new Date("2025,7,27") → interpretado como string pelo parser, pode variar por navegador.

// Ordem de dia/mes na data curta escrita a mao (dd/mm vs mm/dd). So o ingles e
// mes-primeiro; pt e es sao dia-primeiro, como a maior parte do mundo - por isso
// a lista enumera a EXCECAO. O que existia antes era `locale === "pt"` espalhado
// por tres lugares, o que mandava qualquer idioma novo para o formato dos EUA em
// silencio: foi o que quase aconteceu com o espanhol.
export const MONTH_FIRST_LOCALES = new Set(["en"]);

export function isDayFirstLocale(locale) {
  return !MONTH_FIRST_LOCALES.has(String(locale || "").slice(0, 2));
}

export function getDateParts(value, includeTime = false, locale = "en-GB") {
  const timeZone = getTimeZone();
  const date = new Date(value);

  const options = {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    ...(includeTime && {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }),
  };

  const parts = new Intl.DateTimeFormat(locale, options).formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type)?.value || "";

  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: includeTime ? get("hour") : undefined,
    minute: includeTime ? get("minute") : undefined,
  };
}

// formatTime
// recebe timestamp em segundos (Unix), e retorna HH:MM AM ou PM
export function formatTime(timestamp) {
  const date = new Date(timestamp);
  const timeZone = getTimeZone();

  const options = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone,
  };

  return new Intl.DateTimeFormat(i18n.locale, options).format(date);
}

// formatDate
// recebe timestamp em segundos (Unix) e retona data de acordo com o locale do sistema
export function formatDate(timestamp) {
  const date = new Date(timestamp * 1000);
  const timeZone = getTimeZone();

  const options = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone,
  };

  return new Intl.DateTimeFormat(i18n.locale, options).format(date);
}

// formatLongDateFromTimestamp
// recebe um timestamp em segundos (Unix) e retorna a data longa no padrão en-US:
// (1712282386) → "Wednesday, August 27, 2025"
export function formatLongDateFromTimestamp(timestamp) {
  if (!timestamp) return "–";
  const date = new Date(timestamp * 1000);

  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}
export function formatDateTimeBR(value) {
  if (!value) return "-";

  const date = new Date(value);

  if (isNaN(date.getTime())) return "-";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  const hour = String(date.getHours()).padStart(2, "0");
  const minute = String(date.getMinutes()).padStart(2, "0");

  return `${day}/${month}/${year} ${hour}:${minute}`;
}
// formatDateAndTimeFromString
// recebe uma string e retorna a data no padrão en-US, com dia da semana e mês abreviados:
// ("2025-08-27") → "Wed, Aug 27, 2025"
// ("2025-08-27 15:30:00") → "Wed, Aug 27, 2025 at 12:30:00 PM"
// ("2025-08-27T15:30:00Z") → "Wed, Aug 27, 2025 at 12:30:00 PM"
export function formatDateAndTimeFromString(dateInput) {
  const str = String(dateInput || "").trim();
  if (!str) return "Invalid Date";

  // detect time in the input (supports "YYYY-MM-DD HH:mm(:ss)?" or "YYYY-MM-DDTHH:mm(:ss)?")
  const hasTime = /[ T]\d{2}:\d{2}(:\d{2})?/.test(str);

  // normalize: replace space with 'T' and ensure seconds if missing
  let normalized = str;
  if (hasTime) {
    normalized = normalized.replace(/\s+/, "T");
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(normalized)) {
      normalized += ":00";
    }
  }

  // build Date: for date-only, anchor at midnight
  const date = hasTime
    ? new Date(normalized)
    : new Date(`${normalized}T00:00:00`);

  if (isNaN(date)) return "Invalid Date";

  const timeZone = getTimeZone();
  const locale = "en-US";

  const dateOptions = {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "2-digit",
    timeZone,
  };

  if (!hasTime) {
    return date.toLocaleDateString(locale, dateOptions);
  }

  const timeOptions = {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZone,
  };

  const datePart = date.toLocaleDateString(locale, dateOptions);
  const timePart = date.toLocaleTimeString(locale, timeOptions);

  return `${datePart} at ${timePart}`;
}

// formatTimeAndDate
// recebe um timestamp em segundos (Unix) e retorna a data e hora formatadas de acordo com o locale, com ou sem segundos:
// (1712282386)
//  → en-US: "Wed, Apr 04, 2024 at 01:19:46 PM"
//  → pt-BR: "qua., 04 de abr. de 2024 às 13:19:46"
// (1712282386, false)
//  → en-US: "Wed, Apr 04, 2024 at 01:19 PM"
//  → pt-BR: "qua., 04 de abr. de 2024 às 13:19"
export function formatTimeAndDate(epoch, showSeconds = true) {
  const toMs = (v) => {
    if (v == null) return Date.now();
    const n = Number(v);
    if (!isFinite(n)) return Date.now();
    return n > 1e12 ? n : Math.round(n * 1000);
  };

  const date = new Date(toMs(epoch));
  const timeZone = getTimeZone();
  const locale = i18n.locale;

  const dateOptions = {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "2-digit",
    timeZone,
  };

  const timeOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone,
  };

  if (showSeconds) timeOptions.second = "2-digit";

  const formattedDate = new Intl.DateTimeFormat(locale, dateOptions).format(
    date,
  );
  const formattedTime = new Intl.DateTimeFormat(locale, timeOptions).format(
    date,
  );

  return `${formattedDate} ${i18n.t("extras.at")} ${formattedTime}`;
}

export function parseTimestamp(t) {
  if (!t) return 0;

  // format: 20260318T145200
  return new Date(
    t.slice(0, 4),
    t.slice(4, 6) - 1,
    t.slice(6, 8),
    t.slice(9, 11),
    t.slice(11, 13),
  ).getTime();
}

// formatFromCustomString
// recebe diferentes formatos de STRING, transforma em ISO, considera o locale, e retorna hora, data ou ambos:
// ("20250519T133800", "time") → "1:38 PM" (em en-US)
// ("20250519T133800", "date") → "May 19, 2025" (en-US)
// ("20250519T133800") → "May 19, 2025, 1:38 PM" (en-US)

export function formatFromCustomString(dateString, formatType = "full") {
  const { timezone } = useSettingsStore();
  const locale = i18n.locale;

  let date;

  if (/^\d{4}-\d{2}-\d{2}T/.test(dateString)) {
    date = new Date(dateString);
  } else {
    let year,
      month,
      day,
      hour = "00",
      minute = "00";

    if (/^\d{8}T\d{6}$/.test(dateString)) {
      year = dateString.slice(0, 4);
      month = dateString.slice(4, 6);
      day = dateString.slice(6, 8);
      hour = dateString.slice(9, 11);
      minute = dateString.slice(11, 13);
    } else if (/^\d{8}-\d{4}$/.test(dateString)) {
      year = dateString.slice(0, 4);
      month = dateString.slice(4, 6);
      day = dateString.slice(6, 8);
      hour = dateString.slice(9, 11);
      minute = dateString.slice(11, 13);
    } else if (dateString.includes("/")) {
      const parts = dateString.split("/");
      if (isDayFirstLocale(locale)) {
        day = parts[0];
        month = parts[1];
        year = parts[2];
      } else {
        month = parts[0];
        day = parts[1];
        year = parts[2];
      }
    }

    const iso = `${year}-${month}-${day}T${hour}:${minute}:00Z`;
    date = new Date(iso);
  }

  if (isNaN(date.getTime())) return "Invalid Date";

  const timeZone = timezone === "BRT" ? "America/Sao_Paulo" : "UTC";

  const formattedDate = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone,
  }).format(date);

  const formattedTime = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone,
  }).format(date);

  return `${formattedDate}, ${formattedTime}`;
}

export function parseCustomDate(value) {
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(4, 6)) - 1;
  const day = Number(value.slice(6, 8));
  const hour = Number(value.slice(9, 11));
  const minute = Number(value.slice(11, 13));
  const second = Number(value.slice(13, 15));

  return new Date(year, month, day, hour, minute, second);
}

export function normalizeTopic(topic) {
  if (topic.includes("Monetary")) return "Monetary Policy";
  if (
    topic.includes("Economy") ||
    topic.includes("Manufacturing") ||
    topic.includes("Retail")
  )
    return "Economy";
  if (topic.includes("Markets") && topic.includes("Financial"))
    return "Markets";

  if (topic.includes("Finance") || topic.includes("Estate")) return "Finance";

  if (topic.includes("Earnings") || topic.includes("Mergers"))
    return "Corporate";

  if (
    topic.includes("IPO") ||
    topic.includes("Offering") ||
    topic.includes("Listing")
  )
    return "Capital Markets";

  if (topic.includes("Blockchain") || topic.includes("Technology"))
    return "Technology";

  if (topic.includes("Energy")) return "Energy";

  if (topic.includes("Life Sciences")) return "Healthcare";

  if (topic.includes("Real Estate")) return "Real Estate";

  return "Other";
}

export function copyToClipboard (text) {
      if (!text) return
      navigator.clipboard.writeText(text)
    }

/** PLEXUS FORMATTING */

export const getPreviousBusinessDay = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() - 1);

  if (d.getDay() === 6) d.setDate(d.getDate() - 1);
  if (d.getDay() === 0) d.setDate(d.getDate() - 2);

  return d;
};

export const toYYYYMMDD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}${m}${d}`;
};

export const parseISODate = (dateStr) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d);
};

export const normalizeCnpj = (cnpj) => {
  return (cnpj || "").replace(/\D/g, "");
};

export const getDateYYYYMMDD = (daysToAdd, isOrderLog = false) => {
  const timeZone = getTimeZone();
  let current = new Date();
  let daysAdded = 0;

  while (daysAdded < daysToAdd) {
    current.setDate(current.getDate() + 1);
    const weekday = new Intl.DateTimeFormat("en-US", {
      weekday: "short",
      timeZone,
    })
      .formatToParts(current)
      .find((p) => p.type === "weekday")?.value;

    if (weekday !== "Sat" && weekday !== "Sun") {
      daysAdded++;
    }
  }

  const { year, month, day } = getDateParts(current);
  if( isOrderLog) return `${year}-${month}-${day}`;
  return `${year}${month}${day}`;
};
export const convertCurrencyToNumber = (currencyString) => {
  if (typeof currencyString === "string") {
    return parseFloat(currencyString.replace(/[^0-9.-]+/g, ""));
  } else {
    return 0.0;
  }
};

export const formatMoneyAmount = (amount, ccy) => {
  if (amount === null || amount === undefined || amount === "-") return "-";
  const num =
    typeof amount === "number"
      ? amount
      : parseFloat(String(amount).replace(/[^0-9.-]+/g, ""));
  if (isNaN(num)) return "-";
  const prefix = ccy === "USD" ? "$ " : ccy === "EUR" ? "€ " : "";
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Converts a single flat quote_event (SSE) into an rfq_quote item.
// Maps API field rfq_quote_px → internal rfq_quote_px_client.
export const formmatingQuote = (event) => {
  return {
    ...event,
    rfq_quote_px_client: event.rfq_quote_px,
    rfq_ptax: (5.2095 - event.rfq_quote_px).toFixed(4),
  };
};

export const formatSettlement = (sett1, sett2) => {
  const parseDate = (yyyymmdd) => {
    const str = String(yyyymmdd);
    const year = Number(str.slice(0, 4));
    const month = Number(str.slice(4, 6)) - 1;
    const day = Number(str.slice(6, 8));

    return new Date(year, month, day);
  };

  const date1 = parseDate(sett1);
  const date2 = parseDate(sett2);

  const diffMs = date2 - date1;
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  return `D+${diffDays}`;
};

export function formatMaturityDate(dateStr) {
  if (!dateStr || dateStr === "-") return "-";
  if (/^d[+\-]/i.test(dateStr)) return dateStr.toUpperCase();
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("pt-BR");
  } catch (e) {
    return dateStr;
  }
}

export function formatForwardRate(fPoints, spotRate) {
  if (isNaN(fPoints) || isNaN(spotRate)) return "-";
  return (fPoints + spotRate).toFixed(4);
}




/** MATURITY/SETTLEMENT HOLIDAY CHECK (PLEXUS) */

// US federal holidays that always fall on the same calendar date (MMDD)
const US_HOLIDAYS_FIXED = new Set([
  "0101", // New Year's Day
  "0619", // Juneteenth
  "0704", // Independence Day
  "1111", // Veterans Day
  "1225", // Christmas Day
]);

// holidays br
const BR_HOLIDAYS = new Set([
  "0101", // Confraternização Universal
  "0403", // Sexta-feira Santa
  "0421", // Tiradentes
  "0501", // Dia do Trabalho
  "0907", // Independência
  "1012", // Nossa Senhora Aparecida
  "1102", // Finados
  "1120", // republica
  "1115", // Proclamação da República
  "1225", // Natal
]);


// getNthWeekdayOfMonth
// retorna o dia do mês da "n"-ésima ocorrência de um weekday (0=Dom...6=Sáb) em um mês/ano
const getNthWeekdayOfMonth = (year, month, weekday, n) => {
  const date = new Date(year, month, 1);
  let count = 0;
  while (count < n) {
    if (date.getDay() === weekday) count++;
    if (count === n) return date.getDate();
    date.setDate(date.getDate() + 1);
  }
};

// getLastWeekdayOfMonth
// retorna o dia do mês da última ocorrência de um weekday (0=Dom...6=Sáb) em um mês/ano
const getLastWeekdayOfMonth = (year, month, weekday) => {
  const date = new Date(year, month + 1, 0);
  while (date.getDay() !== weekday) date.setDate(date.getDate() - 1);
  return date.getDate();
};

// getUSFloatingHolidays
// feriados federais dos EUA que mudam de data a cada ano, calculados para o ano informado
// retorna um Set no mesmo formato MMDD usado por BR_HOLIDAYS/US_HOLIDAYS_FIXED
const getUSFloatingHolidays = (year) => {
  const mm = (n) => String(n).padStart(2, "0");
  return new Set([
    `01${mm(getNthWeekdayOfMonth(year, 0, 1, 3))}`, // MLK Day: 3ª segunda de janeiro
    `02${mm(getNthWeekdayOfMonth(year, 1, 1, 3))}`, // Presidents' Day: 3ª segunda de fevereiro
    `05${mm(getLastWeekdayOfMonth(year, 4, 1))}`, // Memorial Day: última segunda de maio
    `09${mm(getNthWeekdayOfMonth(year, 8, 1, 1))}`, // Labor Day: 1ª segunda de setembro
    `10${mm(getNthWeekdayOfMonth(year, 9, 1, 2))}`, // Columbus Day: 2ª segunda de outubro
    `11${mm(getNthWeekdayOfMonth(year, 10, 4, 4))}`, // Thanksgiving: 4ª quinta de novembro
  ]);
};

// toMMDD
// extrai "MMDD" de uma data, no formato usado pelos Sets de feriados
const toMMDD = (date) => toYYYYMMDD(date).slice(4);

// toSettlementDate
// normaliza o valor de maturity/settlement do Plexus para um objeto Date
// aceita Date, "YYYYMMDD" (ex: rfq_settdate_ccy1/ccy2) ou "YYYY-MM-DD"
const toSettlementDate = (value) => {
  if (value instanceof Date) return value;
  const str = String(value);
  if (/^\d{8}$/.test(str)) {
    return new Date(
      Number(str.slice(0, 4)),
      Number(str.slice(4, 6)) - 1,
      Number(str.slice(6, 8)),
    );
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return parseISODate(str.slice(0, 10));
  return new Date(str);
};

// getCurrentDate
// retorna a data atual do sistema como objeto Date
export const getCurrentDate = () => new Date();

export const isBrazilHoliday = (date) => BR_HOLIDAYS.has(toMMDD(date));

const isWeekend = (date) => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

export const isUSHoliday = (date) => {
  const mmdd = toMMDD(date);
  return (
    US_HOLIDAYS_FIXED.has(mmdd) ||
    getUSFloatingHolidays(date.getFullYear()).has(mmdd)
  );
};

// recebe a data de maturity/settlement selecionada no Plexus (Date, "YYYYMMDD" ou "YYYY-MM-DD")
// se nenhuma data for informada, usa a data atual (getCurrentDate)
// retorna true se a data cair em final de semana ou em feriado no Brasil OU nos EUA, false caso contrário
export const isMaturityHoliday = (maturityOrSettlementDate) => {
  const date = toSettlementDate(maturityOrSettlementDate ?? getCurrentDate());
  return isWeekend(date) || isBrazilHoliday(date) || isUSHoliday(date);
};


/** flow Signt */
export function formatCompactAmount(value) {
  const number = Number(value)

  if (!Number.isFinite(number)) return '0.00'

  const sign = number < 0 ? '-' : ''
  const abs = Math.abs(number)

  if (abs >= 1e12) return `${sign}${(abs / 1e12).toFixed(2)}Tri`
  if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(2)}Bi`
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(2)}Mi`
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(2)}K`

  return `${sign}${abs.toFixed(2)}`
}
